import { useState, useEffect, useCallback } from "react";
import {
  Megaphone, Plus, Trash2, Target, TrendingUp, Sparkles,
  Loader2, AlertCircle, BarChart3, Pause, Play,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { toast } from "sonner";

export default function GoogleAdsTab({ api, onAskAgent }) {
  const [stats, setStats] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newAcc, setNewAcc] = useState({ name: "", customer_id: "" });
  const [loading, setLoading] = useState(true);
  const [campaignLoading, setCampaignLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.allSettled([api.get("/google/ads/stats"), api.get("/google/ads/accounts")]);
      if (s.status === "fulfilled") setStats(s.value.data);
      if (a.status === "fulfilled") setAccounts(a.value.data || []);
    } catch (e) { /* */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const loadCampaigns = useCallback(async (acc) => {
    setSelectedAcc(acc);
    setCampaignLoading(true);
    try {
      const r = await api.get(`/google/ads/accounts/${acc.account_id}/campaigns`);
      setCampaigns(r.data || []);
    } catch (e) { /* */ }
    setCampaignLoading(false);
  }, [api]);

  const handleCreate = async () => {
    if (!newAcc.name) { toast.error("Nom requis"); return; }
    try {
      await api.post("/google/ads/accounts", newAcc);
      toast.success("Compte ajoute");
      setShowNew(false);
      setNewAcc({ name: "", customer_id: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (acc_id) => {
    if (!window.confirm("Supprimer ce compte et toutes ses campagnes ?")) return;
    await api.delete(`/google/ads/accounts/${acc_id}`);
    if (selectedAcc?.account_id === acc_id) setSelectedAcc(null);
    load();
  };

  const askAgentForCampaign = (acc) => {
    onAskAgent({
      domain: "ads",
      account: acc.name,
      action: "create_ads_campaign",
    });
  };

  const objectiveColor = {
    search: "bg-[#4285F4]/15 text-[#4285F4]",
    display: "bg-[#FBBC04]/15 text-[#FBBC04]",
    performance_max: "bg-[#34A853]/15 text-[#34A853]",
    shopping: "bg-[#EA4335]/15 text-[#EA4335]",
  };

  return (
    <div className="space-y-5" data-testid="google-ads-tab">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Comptes", value: stats.total_accounts, color: "text-white" },
            { label: "Campagnes actives", value: `${stats.active_campaigns}/${stats.total_campaigns}`, color: "text-emerald-400" },
            { label: "Cout 30j (EUR)", value: stats.cost_30d_eur.toLocaleString("fr-FR"), color: "text-[#f7c935]" },
            { label: "Conversions / CPA", value: `${stats.conversions_30d} / ${stats.cpa_30d_eur} EUR`, color: "text-white" },
          ].map((s, i) => (
            <div key={i} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`ads-stat-${i}`}>
              <div className="text-[10px] uppercase text-slate-500 font-bold tracking-wider mb-1">{s.label}</div>
              <div className={`text-xl font-black ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-white">Comptes Google Ads</h3>
          <p className="text-xs text-slate-500 mt-0.5">Pilotez vos campagnes et faites drafter de nouvelles annonces par l'agent IA.</p>
        </div>
        <Button onClick={() => setShowNew(!showNew)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="ads-add-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter un compte
        </Button>
      </div>

      {showNew && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4 space-y-3" data-testid="ads-new-form">
          <Input placeholder="Nom du compte" value={newAcc.name} onChange={e => setNewAcc({ ...newAcc, name: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" data-testid="ads-input-name" />
          <Input placeholder="Customer ID Google Ads (Phase 2)" value={newAcc.customer_id} onChange={e => setNewAcc({ ...newAcc, customer_id: e.target.value })} className="bg-[#0b0811] border-white/10 text-white" />
          <div className="flex gap-2">
            <Button onClick={handleCreate} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="ads-confirm-btn">Ajouter</Button>
            <Button onClick={() => setShowNew(false)} variant="outline" className="border-white/10 text-white">Annuler</Button>
          </div>
          <div className="text-[10px] text-amber-300/80 bg-amber-500/5 border border-amber-500/15 rounded p-2">
            <strong>Phase 1 :</strong> donnees demonstration et drafting IA. Phase 2 : connexion OAuth + Developer Token Google Ads pour campagnes reelles.
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid gap-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
          <Megaphone className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Aucun compte enregistre.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {accounts.map(acc => (
            <div
              key={acc.account_id}
              className={`bg-[#14101c] border rounded-xl p-4 transition-all cursor-pointer ${selectedAcc?.account_id === acc.account_id ? "border-[#f7c935]/50" : "border-white/5 hover:border-white/10"}`}
              onClick={() => loadCampaigns(acc)}
              data-testid={`ads-account-${acc.account_id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-[#0b0811] flex items-center justify-center flex-shrink-0">
                    <Megaphone className="w-5 h-5 text-[#34A853]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-white">{acc.name}</div>
                    {acc.customer_id && <div className="text-xs text-slate-500">CID: {acc.customer_id}</div>}
                    <div className="text-[10px] text-slate-500 mt-0.5">{acc.is_connected ? "Connecte" : "Mode demo (Phase 1)"}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={(e) => { e.stopPropagation(); askAgentForCampaign(acc); }} size="sm" className="bg-gradient-to-r from-[#4285F4] to-[#EA4335] hover:opacity-90 text-white text-xs h-8" data-testid={`ads-ai-campaign-${acc.account_id}`}>
                    <Sparkles className="w-3 h-3 mr-1" /> Creer campagne IA
                  </Button>
                  <Button onClick={(e) => { e.stopPropagation(); handleDelete(acc.account_id); }} size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Campaigns panel */}
      {selectedAcc && (
        <div className="bg-[#14101c] border border-[#f7c935]/30 rounded-xl p-4" data-testid="ads-campaigns-panel">
          <h4 className="text-sm font-bold text-white mb-3 flex items-center gap-2"><Target className="w-4 h-4 text-[#f7c935]" /> Campagnes — {selectedAcc.name}</h4>
          {campaignLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
          ) : campaigns.length === 0 ? (
            <p className="text-xs text-slate-500">Aucune campagne. Cliquez sur "Creer campagne IA" pour qu'un draft soit propose par l'agent.</p>
          ) : (
            <div className="space-y-2">
              {campaigns.map(c => {
                const m = c.metrics_30d;
                return (
                  <div key={c.campaign_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`ads-campaign-${c.campaign_id}`}>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {c.status === "active" ? <Play className="w-3 h-3 text-emerald-400 flex-shrink-0" /> : <Pause className="w-3 h-3 text-slate-500 flex-shrink-0" />}
                        <span className="text-sm font-bold text-white truncate">{c.name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${objectiveColor[c.objective] || "bg-slate-500/15 text-slate-400"}`}>
                          {c.objective.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-bold text-[#f7c935]">{c.daily_budget_eur} EUR/j</div>
                      </div>
                    </div>
                    {m && (
                      <div className="grid grid-cols-5 gap-2 text-center">
                        <div>
                          <div className="text-[9px] uppercase text-slate-500">Impressions</div>
                          <div className="text-xs font-bold text-white">{m.impressions.toLocaleString("fr-FR")}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-slate-500">Clics</div>
                          <div className="text-xs font-bold text-white">{m.clicks}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-slate-500">CTR</div>
                          <div className="text-xs font-bold text-emerald-400">{m.ctr}%</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-slate-500">Conv.</div>
                          <div className="text-xs font-bold text-white">{m.conversions}</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-slate-500">CPA</div>
                          <div className="text-xs font-bold text-[#f7c935]">{m.cpa_eur} EUR</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
